import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Collection, CollectionAction, CollectionDocument, DocumentUploadModel, TextItem } from '../../interfaces/collection.interface';
import { ModalService } from '../../services/modal.service';
import { UploadDropdownComponent } from '../../../../shared/components/upload-dropdown/upload-dropdown.component';
import { UploadOption } from '../../../../shared/interfaces/upload.interface';
import { CollectionsService } from '../../services/collections.service';
import { ToastrService } from 'ngx-toastr';
import { AUTH_MESSAGES } from '../../../../shared/constants/message.constants';
import { DocumentService } from '../../services/document.service';
import { DocumentUploadRequest } from '../../interfaces/document.interface';
import { CollectionName } from '../../../../shared/enums/collection-name.enum';
import { HttpResponse } from '@angular/common/http';
import { FileService } from '../../../../shared/services/file.service';

@Component({
  selector: 'app-collection-card',
  standalone: true,
  imports: [CommonModule, UploadDropdownComponent],
  templateUrl: './collection-card.component.html',
  styleUrl: './collection-card.component.css'
})
export class CollectionCardComponent {
  @Input() collection!: Collection;
  @Output() actionPerformed = new EventEmitter<CollectionAction>();
  @Output() reloadCollections = new EventEmitter<CollectionAction>();
  @Input() collectionNumber!: number;

  selected: 'documents' | 'texts' = 'documents';
  showUploadDropdown = false;
  isBatchUploading = false;
  activeUploadCount = 0;
  private deletingDocumentKeys = new Set<string>();
  private expandedGroups: Record<string, boolean> = {};
  hoveredTextId: string | null = null;

  constructor(
    private modalService: ModalService, 
    private collectionService: CollectionsService, 
    private documentService: DocumentService, 
    private toastr: ToastrService,
    private fileService: FileService
  ) { }

  onToggleExpansion(): void {
    this.collection.isExpanded = !this.collection.isExpanded; // local update
    this.actionPerformed.emit({
      type: this.collection.isExpanded ? 'expand' : 'collapse',
      collectionId: this.collection.id
    });
  }
  setActive(option: 'documents' | 'texts') {
    this.selected = option;
  }

  onAddDocument(): void {
    this.showUploadDropdown = !this.showUploadDropdown;
  }

  onUploadOptionSelected(option: UploadOption, targetGroup?: string, documentName?: string, isReadOnly: boolean = false): void {
    this.showUploadDropdown = false;

    if (option.id === 'document') {
      this.openDocumentUploadModal(targetGroup, isReadOnly ?? false, documentName);
    } else if (option.id === 'chunk') {
      this.openChunkUploadModal();
    }
  }
  // private openDocumentUploadModal(targetGroup?: string): void {
  //   this.modalService.openUploadDocumentModal(this.collection.name)
  //     .then((result: { file: File; documentName: string }) => {
  //       if (!result) return;

  //       // ✅ Safely cast collection name to enum
  //       const payload: DocumentUploadRequest = {
  //         file: result.file,
  //         documentName: result.documentName.trim().replace(/\s+/g, '_'),
  //         collectionName: this.collection.name as CollectionName
  //       };

  //       // 1️⃣ Add a dummy uploading document immediately
  //       const newDoc = {
  //         documentName: payload.documentName,
  //         file: payload.file,
  //         targetGroup,
  //         uploadFailed: false
  //       };
  //       this.addDocumentToCollection(newDoc);

  //       // 2️⃣ Start the real upload to backend
  //       this.documentService.uploadDocument(payload).subscribe({
  //         next: (res) => {
  //           // ✅ Mark as completed and update from backend
  //           const doc = this.collection.documents.find(
  //             d => d.name === payload.documentName && d.status === 'uploading'
  //           );
  //           if (doc) {
  //             doc.status = 'completed';
  //             doc.uploadProgress = 100;
  //           }
  //         },
  //         error: (err) => {
  //           this.toastr.error('Document upload failed.');

  //           const index = this.collection.documents.findIndex(
  //             d => d.name === payload.documentName && d.status === 'uploading'
  //           );

  //           if (index > -1) {
  //             const failedDoc: CollectionDocument = {
  //               ...this.collection.documents[index],
  //               status: 'failed' as 'failed'
  //             };

  //             // Replace document immutably
  //             this.collection.documents = [
  //               ...this.collection.documents.slice(0, index),
  //               failedDoc,
  //               ...this.collection.documents.slice(index + 1),
  //             ];

  //             // ⏱️ Remove after 2 seconds
  //             setTimeout(() => {
  //               this.collection.documents = this.collection.documents.filter((_, i) => i !== index);
  //             }, 2000);
  //           }
  //         }

  //       });
  //     })
  //     .catch((err) => {
  //       console.warn('Upload modal was closed or failed:', err);
  //     });
  // }
  private openDocumentUploadModal(targetGroup?: string, isReadOnly: boolean = false, documentName?: string): void {
    const categories = Array.from(new Set((this.collection.documents || []).map(d => d.category).filter(Boolean) as string[]));
    this.modalService.openUploadDocumentModal(this.collection.name, isReadOnly, documentName, categories)
      .then((result: { uploads: Array<{ file: File; documentName: string; category: string }> }) => {
        if (!result?.uploads?.length) return;

        this.isBatchUploading = true;
        this.activeUploadCount = result.uploads.length;

        let successCount = 0;
        let failureCount = 0;

        result.uploads.forEach((uploadItem) => {
          const payload: DocumentUploadRequest = {
            file: uploadItem.file,
            documentName: uploadItem.documentName.trim().replace(/\s+/g, '_'),
            collectionName: this.collection.name as CollectionName,
            category: uploadItem.category,
            extension: this.fileService.getFileExtension(uploadItem.file.name).toLowerCase()
          };

          const tempId = this.addDocumentToCollection({
            documentName: payload.documentName,
            category: payload.category,
            file: payload.file,
            targetGroup,
            uploadFailed: false
          });

          this.documentService.uploadDocument(payload).subscribe({
            next: (res) => {
              successCount += 1;
              this.markUploadCompleted(tempId, res);
              this.finalizeUploadTick(result.uploads.length, successCount, failureCount);
            },
            error: () => {
              failureCount += 1;
              this.markUploadFailed(tempId);
              this.finalizeUploadTick(result.uploads.length, successCount, failureCount);
            }
          });
        });
      })
      .catch((err) => {
        console.warn('Upload modal was closed or failed:', err);
      });
  }
  private openChunkUploadModal(): void {
    this.modalService.openUploadChunkModal(this.collection.name)
      .then((result) => {
        if (result) {
          // Add the uploaded chunk directly to the local collection
          this.addChunkToCollection(result);

          this.actionPerformed.emit({
            type: 'add_chunk',
            collectionId: this.collection.id,
            data: result
          });
        }
      })
      .catch(() => {
      });
  }

 onDownloadDocument(data: CollectionDocument): void {
    this.documentService.downloadDocument(this.collection.name, data.name).subscribe({
    next: (response: HttpResponse<Blob>) => {
      const blob = response.body;
      if (!blob) return;
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = data.name;
      
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches?.[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    error: (error) => {
      console.error('Download failed:', error);
    }
  });
}
  onDeleteDocument(document: CollectionDocument): void {
    if (this.isDeletingDocument(document)) {
      return;
    }

    const title = 'Delete Document';
    const message = 'Are you sure you want to delete this document from the knowledge base? The assistant will no longer use this data for answering queries.';

    this.modalService.openDeleteConfirmModal(document.name, title, message)
      .then((confirmed) => {
        if (confirmed) {
          const deleteKey = this.getDocumentDeleteKey(document);
          this.deletingDocumentKeys.add(deleteKey);

          const payload = {
            collection_id: this.collection.id,
            collection_name: this.collection.name,
            object_id: document,
            document_name: document.name
          };

          this.collectionService.deleteDocument(payload).subscribe({
            next: () => {
              this.removeDocumentFromCollection(document);
              this.deletingDocumentKeys.delete(deleteKey);
              this.toastr.success(AUTH_MESSAGES.DELETE_DOCUMENT_SUCCESS);
              this.reloadCollections.emit(); // refresh list after delete
            },
            error: (err) => {
              this.deletingDocumentKeys.delete(deleteKey);
              console.error('Failed to delete document', err);
              this.toastr.error(AUTH_MESSAGES.DELETE_DOCUMENT_ERROR);
            }
          });
        }
      })
      .catch(() => { /* modal dismissed */ });
  }



  onActivateDocument(document: any): void {
    this.modalService.openActivateDocumentModal(this.collection.name, document.name)
      .then((confirmed) => {
        if (confirmed) {
          this.actionPerformed.emit({
            type: 'activate_document',
            collectionId: this.collection.id,
            documentId: document.id
          });
        }
      })
      .catch(() => { });

  }

  onViewDocument(document: CollectionDocument): void {
    this.modalService.openViewDocumentModal(
      this.collection.name,
      document
    ).catch(() => { });
  }

  onDeleteText(text: TextItem): void {
    this.modalService.openDeleteTextModal(text.heading)
      .then((confirmed) => {
        if (confirmed) {
          const payload = {
            collection_id: this.collection.id,
            collection_name: this.collection.name,
            object_id: text.vectorKey
          }

          this.collectionService.deleteText(payload).subscribe({
            next: (res) => {
              this.toastr.success(AUTH_MESSAGES.DELETE_TEXT_SUCCESS);
              this.reloadCollections.emit();
            },
            error: (err) => {
              this.toastr.error(AUTH_MESSAGES.DELETE_TEXT_ERROR);
            }
          });
        }
      })
      .catch(() => { });
  }
  onEditText(text: TextItem): void {
    this.modalService.openEditTextModal({ heading: text.heading, text: text.text })
      .then((result) => {
        if (result) {
          const payload = {
            collection_id: this.collection.id,
            collection_name: this.collection.name,
            object_id: text.vectorKey,
            text: result.text,
            parent_heading: text.heading
          };

          this.collectionService.updateText(payload).subscribe({
            next: (res) => {
              this.toastr.success(res.message);
              this.reloadCollections.emit();
            },
            error: (err) => {
              this.toastr.error(err);
            }
          });
        }
      })
      .catch(() => { });
  }

  formatDate(date: Date | string): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    }).format(d);
  }

  formatDateTime(date: Date | string): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(d).replace(' at ', ' ');
  }

  trackByDocumentId(index: number, document: any): string {
    return document.id;
  }
  getDisplayDocumentName(document: CollectionDocument): string {
    return (document.name || '').split('_').join(' ');
  }

  getDocumentCategory(document: CollectionDocument): string {
    return document.category || '-';
  }

  get groupedDocuments(): Array<{ baseName: string, name: string, documents: any[] }> {
    const groups: { [key: string]: { name: string, documents: any[] } } = {};

    // Group documents by BOTH baseName AND name to ensure same-named docs stay together
    for (const doc of this.collection.documents) {
      const baseNameKey = doc.baseName || doc.name;
      const fullKey = `${baseNameKey}___${doc.name}`; // Use combined key

      if (!groups[fullKey]) {
        groups[fullKey] = {
          name: doc.name,
          documents: []
        };
      }
      groups[fullKey].documents.push(doc);
    }

    // Convert object to array and sort documents within each group
    return Object.keys(groups)
      .map(fullKey => {
        const baseName = fullKey.split('___')[0]; // Extract baseName for expansion tracking
        return {
          baseName: fullKey, // Use fullKey as unique identifier
          name: groups[fullKey].name,
          documents: groups[fullKey].documents.sort((a, b) => {
            const statusOrder: { [key: string]: number } = { 'uploading': 0, 'success': 1, 'failed': 2 };
            const aStatus = a.status || 'success';
            const bStatus = b.status || 'success';
            if (statusOrder[aStatus] !== statusOrder[bStatus]) {
              return statusOrder[aStatus] - statusOrder[bStatus];
            }
            return (b.version || '').localeCompare(a.version || '');
          })
        };
      });
  }


  isGroupExpanded(baseName: string): boolean {
    return !!this.expandedGroups[baseName];
  }

  toggleGroupExpansion(baseName: string): void {
    this.expandedGroups[baseName] = !this.expandedGroups[baseName];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const uploadContainer = target.closest('.upload-container');

    if (!uploadContainer && this.showUploadDropdown) {
      this.showUploadDropdown = false;
    }
  }

  private addDocumentToCollection(uploadResult: { documentName: string, category: string, file: File, uploadFailed?: boolean, targetGroup?: string }): string {
    // Determine the baseName - if targetGroup is provided, use it; otherwise use documentName
    const baseName = uploadResult.targetGroup || uploadResult.documentName;

    // Find the highest version number in the same group to increment it
    const existingVersions = this.collection.documents
      .filter(doc => (doc.baseName || doc.name) === baseName)
      .map(doc => doc.version || '1.0')
      .map(version => {
        const parts = version.split('.');
        return parseInt(parts[0]) || 1;
      });

    const nextVersion = existingVersions.length > 0 ? Math.max(...existingVersions) + 1 : 1;

    // Create a new document object with uploading status
    const temporaryId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newDocument = {
      id: temporaryId, // Temporary ID until server responds
      name: uploadResult.documentName,
      category: uploadResult.category,
      baseName: uploadResult.file.name,
      size: this.formatFileSize(uploadResult.file.size),
      type: uploadResult.file.type || 'application/pdf',
      uploadedAt: new Date(),
      lastModified: new Date(),
      version: `${nextVersion}.0`,
      isActiveDocument: false,
      status: uploadResult.uploadFailed ? 'failed' : 'uploading',
      uploadProgress: uploadResult.uploadFailed ? 0 : 0
    };

    // Add the document to the collection's documents array at the beginning
    // This ensures it appears as the first row in the group
    this.collection.documents = [newDocument as any, ...this.collection.documents];

    // Update the objects included count in metadata
    this.collection.metadata.objectsIncluded = this.collection.documents.length;

    // Update the last accessed time
    this.collection.metadata.lastAccessedAt = new Date();

    // Auto-expand the group when a new document is added
    this.expandedGroups[baseName] = true;

    // // If not failed, simulate upload progress
    // if (!uploadResult.uploadFailed) {
    //   this.simulateUploadProgress(newDocument.id);
    // }

    return temporaryId;
  }

  private markUploadCompleted(tempId: string, response: any): void {
    const index = this.collection.documents.findIndex(d => d.id === tempId && d.status === 'uploading');
    if (index === -1) {
      return;
    }

    const completedDoc: CollectionDocument = {
      ...this.collection.documents[index],
      ...(response?.data || {}),
      status: 'completed',
      uploadProgress: 100
    };

    this.collection.documents = [
      ...this.collection.documents.slice(0, index),
      completedDoc,
      ...this.collection.documents.slice(index + 1),
    ];

    setTimeout(() => {
      const completedIndex = this.collection.documents.findIndex(d => d.id === tempId && d.status === 'completed');
      if (completedIndex > -1) {
        this.collection.documents = [
          ...this.collection.documents.slice(0, completedIndex),
          {
            ...this.collection.documents[completedIndex],
            status: undefined,
            uploadProgress: undefined
          },
          ...this.collection.documents.slice(completedIndex + 1),
        ];
      }
    }, 2000);
  }

  private markUploadFailed(tempId: string): void {
    const index = this.collection.documents.findIndex(d => d.id === tempId && d.status === 'uploading');
    if (index === -1) {
      return;
    }

    const failedDoc: CollectionDocument = {
      ...this.collection.documents[index],
      status: 'failed'
    };

    this.collection.documents = [
      ...this.collection.documents.slice(0, index),
      failedDoc,
      ...this.collection.documents.slice(index + 1),
    ];

    setTimeout(() => {
      const failedIndex = this.collection.documents.findIndex(d => d.id === tempId && d.status === 'failed');
      if (failedIndex > -1) {
        this.collection.documents = this.collection.documents.filter((_, i) => i !== failedIndex);
      }
    }, 2000);
  }

  private finalizeUploadTick(totalUploads: number, successCount: number, failureCount: number): void {
    this.activeUploadCount = Math.max(totalUploads - successCount - failureCount, 0);

    if (successCount + failureCount !== totalUploads) {
      return;
    }

    this.isBatchUploading = false;

    if (failureCount === 0) {
      this.toastr.success(`${successCount} document(s) uploaded successfully.`);
      return;
    }

    if (successCount === 0) {
      this.toastr.error('All selected documents failed to upload.');
      return;
    }

    this.toastr.warning(`${successCount} document(s) uploaded, ${failureCount} failed.`);
  }

  private addChunkToCollection(uploadResult: { chunkName: string, chunkContent: string }): void {
    // Create a new text item object with hardcoded values
    const newTextItem = {
      id: `temp-text-${Date.now()}`, // Temporary ID until server responds
      heading: uploadResult.chunkName,
      text: uploadResult.chunkContent,
      uploadedAt: new Date()
    };

    // Add the text item to the collection's texts array
    if (!this.collection.texts) {
      this.collection.texts = [];
    }
    this.collection.texts = [...this.collection.texts, newTextItem];

    // Update the objects included count in metadata
    this.collection.metadata.objectsIncluded = (this.collection.documents?.length || 0) + (this.collection.texts?.length || 0);

    // Update the last accessed time
    this.collection.metadata.lastAccessedAt = new Date();
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  isDeletingDocument(document: CollectionDocument): boolean {
    return this.deletingDocumentKeys.has(this.getDocumentDeleteKey(document));
  }

  private getDocumentDeleteKey(document: CollectionDocument): string {
    return document.id || `${document.name}_${document.uploadedAt}`;
  }

  private removeDocumentFromCollection(document: CollectionDocument): void {
    const deleteKey = this.getDocumentDeleteKey(document);
    this.collection.documents = this.collection.documents.filter(
      current => this.getDocumentDeleteKey(current) !== deleteKey
    );

    this.collection.metadata.objectsIncluded = (this.collection.documents?.length || 0) + (this.collection.texts?.length || 0);
    this.collection.metadata.lastAccessedAt = new Date();
  }
}
